import { useEffect, useMemo, useState } from 'react'

import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import {
  useAccessTokenRoles,
  useDeliveryMethodModal,
} from '@b-stock/fe-core/bstock-next/components'
import {
  isZeroQuotePlaceholder,
  useFlag,
} from '@b-stock/fe-core/bstock-next/helpers'
import {
  activeSellerSubscriptionServiceQuery,
  getSellerCollectsConfig,
  type OrchestratedAuctionListingDetails,
  type OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'
import {
  useIsPast,
  Banner,
  Button,
  FormattedMessage,
  Modal,
  parseOptionalISODate,
  showSnackbar,
} from '@b-stock/fe-core/bstock-react'
import {
  Breakpoints,
  Shadows,
  Typography,
} from '@b-stock/fe-core/design-system'
import { designColors } from '@b-stock/fe-core/theme'
import {
  DocumentDtoDocTypeEnum,
  ShippingTransportModeEnum,
} from '@b-stock/listing-api-client'
import { PayMethodTypeEnum } from '@b-stock/payments-methods-api-client'
import { ShipmentType } from '@b-stock/shipment-api-client'
import type { ActiveServiceDto } from '@b-stock/subscription-api-client'

import { CardOrder } from '@/buyer-portal/components/CardOrder'
import { paysViaSellerRep } from '@/buyer-portal/components/features/listing/acceptedPaymentMethods'
import { getCostContextAnalyticsProperties } from '@/buyer-portal/components/features/listing/helpers/getCostContextAnalyticsProperties'
import { useAnyAddressWithinRadius } from '@/buyer-portal/components/features/listing/hooks/useAnyAddressWithinRadius'
import { useCalculatedAdditionalCharges } from '@/buyer-portal/components/features/listing/hooks/useCalculatedAdditionalCharges'
import { useFirstDibsPurchase } from '@/buyer-portal/components/features/listing/hooks/useFirstDibsPurchase'
import {
  getSellerBilledShippingCents,
  useSellerCollectsPaymentInfo,
} from '@/buyer-portal/components/features/listing/hooks/useSellerCollectsPaymentInfo'
import { useShippingModalTracking } from '@/buyer-portal/components/features/listing/hooks/useShippingModalTracking'
import { useSuppressShippingLine } from '@/buyer-portal/components/features/listing/hooks/useSuppressShippingLine'
import { getTlcTotals } from '@/buyer-portal/components/features/listing/hooks/useTlcTotals'
import { useTotalPaymentInfo } from '@/buyer-portal/components/features/listing/hooks/useTotalPaymentInfo'
import MaxTaxExposureDisplayContainer from '@/buyer-portal/components/features/listing/MaxTaxExposureDisplay'
import useMaxTaxExposure from '@/buyer-portal/components/features/listing/MaxTaxExposureDisplay/useMaxTaxExposure'
import MaxVatExposureDisplayContainer from '@/buyer-portal/components/features/listing/MaxVatExposureDisplay'
import getMaxVatCents from '@/buyer-portal/components/features/listing/MaxVatExposureDisplay/getMaxVatCents'
import useEuMaxVatExposure from '@/buyer-portal/components/features/listing/MaxVatExposureDisplay/useEuMaxVatExposure'
import { getSellerCollectsSellerName } from '@/buyer-portal/components/features/listing/sellerCollectsSellerName'
import { BalanceWidgetContainer } from '@/buyer-portal/components/features/order/checkout/PaymentMethods/helpers/BalanceWidgetContainer'
import { isPaymentMethodDisabled } from '@/buyer-portal/components/features/order/checkout/PaymentMethods/utils'
import { useParcelRateFlow } from '@/buyer-portal/components/ParcelRateList'
import isCanadianTaxCurrency from '@/buyer-portal/helpers/canadianTaxCurrency'
import isEuTaxCurrency from '@/buyer-portal/helpers/euTaxCurrency'
import {
  buyerPremiumAppliesFor,
  sellerCollectsListingContext,
  useSellerCollectsAnalytics,
} from '@/buyer-portal/helpers/sellerCollectsAnalytics'
import type { FeeModel } from '@/buyer-portal/helpers/sellerCollectsFeeModel'
import {
  feeModelHasBuyerPremium,
  getFeeModel,
} from '@/buyer-portal/helpers/sellerCollectsFeeModel'
import { getEditDeliveryMethodRedirectUri } from '@/buyer-portal/helpers/shipping'
import {
  isSellerCollectsLot,
  useIsSellerCollectsSale,
} from '@/buyer-portal/helpers/useIsSellerCollectsSale'
import { usePaymentRestrictions } from '@/buyer-portal/helpers/usePaymentRestrictions'
import useChangeCarrierModal from '@/buyer-portal/hooks/useChangeCarrierModal'
import { useParcelQuotesEnabled } from '@/buyer-portal/hooks/useParcelQuotesEnabled'
import { FeatureFlags } from '@/helpers/featureFlags'
import { SellerCollectsEvent } from '@/helpers/telemetry/SellerCollectsAnalyticsEvents'

import {
  getContractAutoPayId,
  isBidBlockedOnShippingQuote,
  isContractListing,
} from '../../utils'
import { ChangeButton } from '../AutoPay.ui'
import { useAutoPayMethodContext } from '../AutoPayMethodProvider'
import {
  AutoPayConfirmContent,
  FooterButtonRow,
  FooterCancelButton,
  FooterConfirmButton,
  FooterContainer,
} from '../BidConfirmationModal/AutoPayConfirmContent'
import { PaymentMethodSelectionModal } from '../BidConfirmationModal/PaymentMethodSelectionModal'
import {
  getPaymentDeadlineDays,
  bStockQuotesShipping,
  SELLER_COLLECTS_NS,
} from '../BidConfirmationModal/sellerCollectsBidCopy'
import { getSellerCollectsFeeAccess } from '../BidConfirmationModal/sellerCollectsFeeAccess'
import {
  findFeeCard,
  SellerCollectsFeeSection,
} from '../BidConfirmationModal/SellerCollectsFeeSection'
import {
  sellerNameValue,
  SellerCollectsTotalNote,
} from '../BidConfirmationModal/SellerCollectsSummary'
import {
  ButtonRow,
  QuoteLoadingSpinner,
  useTermsOfPurchaseLink,
} from '../BidConfirmationModal/ui'
import { useBidFormContext } from '../BidFormContext'
import BuyNowAmount from './BuyNowAmount'
import { useTrackBuyNowConfirm } from './useTrackBuyNowConfirm'

const bidNs = 'BidConfirmationModal'
const ns = 'BuyNow.BuyNowConfirmationModal'

/**
 * The intro per fee model, spelled out so the keys are greppable and an i18n
 * extractor can see them — a template built from the model hides them.
 *
 * `unknownFee` cannot be reached from this modal today: the fee model resolves
 * with `isLoggedIn: true`, so loading and error both give `undefined` and the
 * Seller Collects intro is not chosen at all. It is mapped anyway because the
 * map is exhaustive over `FeeModel`, which is what makes a new model a type
 * error here rather than a missing string at runtime.
 */
const SELLER_COLLECTS_INTRO_IDS: Record<FeeModel, string> = {
  feeBearing: `${ns}.header.sellerCollectsDesc.feeBearing`,
  feeFree: `${ns}.header.sellerCollectsDesc.feeFree`,
  unknownFee: `${ns}.header.sellerCollectsDesc.unknownFee`,
}

// --- Legacy styled components (unchanged) ---

const BuyNowModal = styled(Modal)`
  border-radius: 0.5rem;

  @media ${Breakpoints.min.medium} {
    max-width: min(90vw, 43.75rem);
  }

  button {
    cursor: pointer;
  }
`

const InfoList = styled.ul`
  padding: 0;
`

const ModalContent = styled(Modal.Content)`
  padding-top: 1rem;
`

const HeaderDescription = styled.div`
  ${Typography.Body3}
  padding-bottom: 1.5rem;
  border-bottom: 0.0625rem solid ${designColors.neutral.mediumGray};
`

const BuyNowDetailsSection = styled.div`
  padding: 1.5rem 0 0;
`

const BuyNowDetailsSectionContainer = styled.div`
  display: grid;
`

const BuyNowOrderAmount = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 1.5rem;
  border-bottom: 0.0625rem solid ${designColors.neutral.mediumGray};
`

const OrderAmountTitle = styled.div`
  ${Typography.Title3}
`
const OrderAmountTotal = OrderAmountTitle

const ActionContainer = styled(Modal.StyledActionContainer)`
  display: flex;
  justify-content: center;
  border: none;
  ${Shadows.Popovers}

  > * + * {
    margin-left: 1rem;
  }
  @media ${Breakpoints.max.small} {
    display: grid;
  }
`

const Checklist = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  ${Typography.Body3}
  color: ${designColors.neutral.darkGray};
`

const DetailsValue = styled.div`
  position: relative;
  ${Typography.Body3}
  color: ${designColors.neutral.darkGray};

  @media ${Breakpoints.max.small} {
    font-size: 0.7rem;
  }
`

const SubmitButton = styled(Button)`
  font-size: 0.78rem;
  white-space: nowrap;
`

const StyledBanner = styled(Banner)`
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  margin-top: 1rem;
`

const SlimBanner = styled(Banner)`
  padding: 0.75rem 1rem;
  margin-bottom: 1.5rem;
  margin-top: 1rem;
`

const ShippingChangeButtonRow = styled.div`
  display: flex;
  margin-bottom: 1.5rem;
`

const CardOrderContainer = styled.div`
  margin-top: 1rem;
`

export type BuyNowConfirmationModalProps = {
  buyerAccountId?: string
  closeModal: () => void
  currency?: string
  listing: OrchestratedAuctionListingDetails
  onBuyNowConfirm: () => void
  selectedQuote?: OrchestratedQuote | null
  sellerSubscription?: ActiveServiceDto | null
}

const BuyNowConfirmationModal = ({
  buyerAccountId,
  closeModal,
  currency: currencyProp = 'USD',
  listing,
  onBuyNowConfirm,
  selectedQuote,
  sellerSubscription,
}: BuyNowConfirmationModalProps) => {
  const { auction, documents } = listing
  const TermsOfPurchaseLink = useTermsOfPurchaseLink(documents)
  // The `currency` prop is never passed by this modal's caller
  // (useBuyNowConfirmationModal), so it would silently default every listing to
  // 'USD' — the listing is the real source of truth.
  const currency = listing.currency ?? currencyProp
  const isContract = isContractListing(listing)
  const contractAutoPayId = getContractAutoPayId(listing)
  // The listing's own flag, deliberately ungated: these autopay guards
  // predate the epic and must hold whether or not the buyer experience is
  // switched on. `isSellerCollectsSale` is the gated one — pick that for
  // anything the flag owns.
  const listingIsSellerCollects = listing.sellerCollects === true
  const isSellerCollectsSale = useIsSellerCollectsSale(listing)
  const { trackSellerCollectsEvent } = useSellerCollectsAnalytics({
    sellerAccountId: listing.accountId,
    enabled: listingIsSellerCollects,
  })
  const { earlyPrice: fdEarlyPrice } = useFirstDibsPurchase(listing)
  const buyNowPrice = fdEarlyPrice ?? (auction.buyNow?.price || 0)
  const trackBuyNowConfirm = useTrackBuyNowConfirm(listing)
  const anyAddressWithinRadius = useAnyAddressWithinRadius(listing._id)
  const autoPayEnforced = useFlag(FeatureFlags.AUTOPAY_ENFORCED)
  const enableBalanceCreditCards = useFlag(
    FeatureFlags.ENABLE_BALANCE_CREDIT_CARDS
  )
  const contractAutoPayEnabled = useFlag(FeatureFlags.ENABLE_CONTRACT_AUTOPAY)
  const isParcel =
    listing.shipping?.transportMode === ShippingTransportModeEnum.Parcel
  const parcelEnabledForSeller = useParcelQuotesEnabled(listing.accountId)
  const showParcelRates = parcelEnabledForSeller && isParcel
  const autoPayEnabledForListing =
    autoPayEnforced &&
    (!isContract || (contractAutoPayEnabled && !!contractAutoPayId))
  const { formatMessage } = useIntl()
  const { autoQuoteGeneration } = useBidFormContext()

  const { editDeliveryMethod, deliveryMethodChangePending } =
    useDeliveryMethodModal()
  const shippingTrackingCallbacks = useShippingModalTracking(listing)

  const {
    defaultPaymentMethod,
    paymentMethods,
    isSavedMethodInvalid,
    selectedPaymentMethodId,
    setSelectedPaymentMethodId,
    isWireSelected,
    setIsWireSelected,
    payableAmountInCents,
    listingIsCreditCardEnabled,
    showAchBlockedWireFallbackMessage,
  } = useAutoPayMethodContext()

  const [isPaymentMethodModalOpen, setIsPaymentMethodModalOpen] =
    useState(false)
  const [isChangeCarrierModalOpen, setIsChangeCarrierModalOpen] =
    useState(false)
  const [isChangingCarrier, setIsChangingCarrier] = useState(false)

  // Local state: FF-on initializes from context (persisted); FF-off from account default
  // If wire was previously selected, start with null (wire bypasses autoPayMethodId)
  const [autoPayMethodId, setAutoPayMethodId] = useState<string | null>(
    autoPayEnabledForListing
      ? isWireSelected
        ? null
        : (selectedPaymentMethodId ?? defaultPaymentMethod?.id ?? null)
      : (defaultPaymentMethod?.id ?? null)
  )

  useEffect(() => {
    if (autoPayEnabledForListing) {
      // Invariant: context owns the FF-on selection; autoPayMethodId mirrors it.
      // Selection handlers must update selectedPaymentMethodId/isWireSelected
      // alongside local state (see handlePaymentMethodConfirm/handleWireConfirmFromModal).
      // Do not preserve a non-null local value here: async wire initialization
      // must clear a stale digital method so it cannot block confirmation.
      // oxlint-disable-next-line react/set-state-in-effect
      setAutoPayMethodId(
        isWireSelected
          ? null
          : (selectedPaymentMethodId ?? defaultPaymentMethod?.id ?? null)
      )
    } else if (isSellerCollectsSale) {
      // Seller Collects opens the picker on this path too, so fill in the
      // default when it loads async but never overwrite the method the buyer
      // picked with Change — that pick also moves `selectedPaymentMethodId`.
      setAutoPayMethodId(
        (current) => current ?? defaultPaymentMethod?.id ?? null
      )
    } else {
      // Legacy: always sync with default when it loads async
      setAutoPayMethodId(defaultPaymentMethod?.id ?? null)
    }
  }, [
    defaultPaymentMethod?.id,
    selectedPaymentMethodId,
    autoPayEnabledForListing,
    isSellerCollectsSale,
    isWireSelected,
  ])

  const isZeroQuoteValue = isZeroQuotePlaceholder(selectedQuote)
  // Without a valid shipping quote we can't calculate the total, so fall
  // back to the pre-autopay (invoice later) UX even when the FF is on.
  const isShippingQuoteAvailable = !!selectedQuote && !isZeroQuoteValue
  const showAutoPayUI =
    autoPayEnabledForListing &&
    isShippingQuoteAvailable &&
    !listingIsSellerCollects

  // A shipping quote is actively being generated and none is usable yet. Holding
  // here prevents rendering (then replacing) content derived from a not-yet-ready
  // quote — e.g. the legacy → AutoPay flash on surfaces that open the modal mid-
  // generation, such as the deep-linked auto-open from a bidAmount query param.
  // Mirrors BidConfirmationModal's equivalent guard for the regular bid flow,
  // including holding for seller-collects: the same generation call still
  // writes the backend's quote-selection record, so confirming before it
  // settles can race ahead of that write and 400 with "no shipment quote
  // selected" even though seller-collects never needs the quote itself.
  const showQuoteLoading =
    autoQuoteGeneration.isLoading && !isShippingQuoteAvailable

  const isBuyerArrangedDefault =
    listing.shipping?.allowedShipmentTypes?.[0] === 'BUYER_ARRANGED'

  // A shipping quote is required but generation settled without one (e.g. no
  // eligible address): withhold the modal entirely rather than show buy-now
  // content without a quote.
  const isActiveBuyer = useAccessTokenRoles()?.buyer === 'ACTIVE'
  const bidBlockedOnQuote = isBidBlockedOnShippingQuote({
    isActiveBuyer,
    isSellerCollects: listingIsSellerCollects,
    quoteLoading: autoQuoteGeneration.isLoading,
    selectedQuote,
  })

  // The modal opens on a Buy Now attempt but is withheld for a missing quote;
  // give the buyer the same "missing shipping quote" toast the purchase POST
  // would have. closeModal so a repeat attempt re-toasts.
  useEffect(() => {
    if (bidBlockedOnQuote) {
      showSnackbar({
        content: formatMessage({
          id: 'BuyNow.BuyNowActions.errors.400.noQuoteSelected',
        }),
        options: { autoClose: true, type: 'error' },
      })
      closeModal()
    }
  }, [bidBlockedOnQuote, closeModal, formatMessage])

  // FP-1904: for parcel listings the inline rate list replaces the carrier
  // dropdown, so suppress the carrier-change affordance when showing rates.
  const shouldShowCarrierChange =
    selectedQuote?.shipmentType === ShipmentType.Binding && !showParcelRates

  const {
    ChangeCarrierModal,
    changeCarrierModalProps,
    setSelectedCarrier,
    isCarrierChangePending,
    hasMultipleCarrierOptions,
    isCheapestCarrier,
    quoteSet,
    isQuoteSetLoading,
    isQuoteSetError,
  } = useChangeCarrierModal({
    listingId: listing._id,
    originLocationId: listing.lot.location.id,
    destinationLocationId: selectedQuote?.destination.id ?? '',
    shipmentType: selectedQuote?.shipmentType ?? ShipmentType.Binding,
    currentQuote: selectedQuote ?? undefined,
    accountId: selectedQuote?.accountId ?? '',
  })

  // FP-1904/1906/1910: shared parcel rate-flow logic (rendering, selection, and
  // the payment-blocking states for no-rates / query error / loading).
  const {
    parcelRateList,
    parcelPaymentBlocked,
    isParcelSelectionPending,
    isParcelRatesLoading,
  } = useParcelRateFlow({
    showParcelRates,
    quoteSet,
    selectedQuote,
    isQuoteSetLoading,
    isQuoteSetError,
    isCarrierChangePending,
    setSelectedCarrier,
    currency,
  })

  const handleCarrierChange = async (quoteId: string) => {
    setIsChangeCarrierModalOpen(false)
    setIsChangingCarrier(true)
    try {
      await setSelectedCarrier(quoteId)
    } finally {
      setIsChangingCarrier(false)
    }
  }

  const isBuyNowWindowPast = useIsPast(
    parseOptionalISODate(auction.buyNow?.endTime)
  )
  const displayEndTime =
    !isBuyNowWindowPast && auction.buyNow?.endTime
      ? auction.buyNow.endTime
      : auction.actualEndTime

  const additionalCharges = useCalculatedAdditionalCharges({
    buyersPremium: sellerSubscription?.buyersPremium ?? 0,
    currentBidDollars: buyNowPrice,
    selectedQuote,
  })

  const selectedPaymentMethod = useMemo(
    () => paymentMethods.find((pm) => pm.id === autoPayMethodId) ?? null,
    [paymentMethods, autoPayMethodId]
  )

  const isCreditCardSelected =
    selectedPaymentMethod?.type === PayMethodTypeEnum.Card ||
    selectedPaymentMethod?.type === PayMethodTypeEnum.GetbalanceCc
  const showCcAcceptedBanner =
    enableBalanceCreditCards &&
    listingIsCreditCardEnabled &&
    !isCreditCardSelected

  // FP-2498 — fold Canadian max-tax exposure into Estimated Total; shares
  // the react-query cache with the sibling MaxTaxExposureDisplayContainer.
  const isCanadianTaxApplicable = isCanadianTaxCurrency(currency) && !isContract
  const maxTaxData = useMaxTaxExposure({
    listingId: listing._id,
    formSingleBid: isCanadianTaxApplicable ? Math.round(buyNowPrice * 100) : 0,
    buyerAddressId: isCanadianTaxApplicable
      ? selectedQuote?.destination.id
      : undefined,
    formProxyMaxBid: isCanadianTaxApplicable
      ? Math.round(buyNowPrice * 100)
      : undefined,
    buyerFeeCents: isCanadianTaxApplicable
      ? additionalCharges.bstockFee
      : undefined,
    shippingCents: isCanadianTaxApplicable
      ? selectedQuote?.totalPrice
      : undefined,
    shipmentType: isCanadianTaxApplicable
      ? selectedQuote?.shipmentType
      : undefined,
  })

  // FP-3261 — fold EU/GB max-VAT exposure in the same way, so the figure
  // the buyer confirms matches the VAT-inclusive Order Total (FP-3252).
  // Shares the react-query key with `MaxVatExposureDisplayContainer`.
  const isEuVatApplicable = isEuTaxCurrency(currency) && !isContract
  const maxVatData = useEuMaxVatExposure({
    isTaxable: isEuVatApplicable,
    listingId: listing._id,
    formSingleBid: Math.round(buyNowPrice * 100),
    buyerAddressId: selectedQuote?.destination.id,
    formProxyMaxBid: Math.round(buyNowPrice * 100),
    buyerFeeCents: additionalCharges.bstockFee,
    shippingCents: selectedQuote?.totalPrice,
  })

  // Reverse-charge / unavailable estimates fold in as 0, matching the card.
  const totalTaxCents =
    (maxTaxData?.maxTaxAmount ?? 0) + getMaxVatCents(maxVatData)

  // Fee/TLC use the buy-now price as their basis — the fixed amount this
  // transaction actually charges, not the auction's regular bid basis.
  // minimum_required_bid_usd is the buy-now price too: there's no bid range
  // here, and this is the direct analog of "least amount required to win."
  const costContext = getCostContextAnalyticsProperties({
    currentBidUsd: auction.winningBidAmount ?? null,
    minimumRequiredBidUsd: buyNowPrice,
    bstockFeeCents: additionalCharges.bstockFee,
    totalLandedCostUsd: getTlcTotals({
      additionalChargesTotal: additionalCharges.total,
      currentBidDollars: buyNowPrice,
      tlcTaxDollars: totalTaxCents / 100,
    }).totalLandedCostWithTax,
  })

  // FP-3356 — B-Stock only quotes freight it books itself, so on every other
  // Seller Collects mode a pickup quote of `totalPrice: 0` would print here
  // as "$0.00" while the fee model is still resolving.
  const suppressShippingLine = useSuppressShippingLine(listing, selectedQuote)

  const totalPaymentInfo = useTotalPaymentInfo({
    purchaseAmountDollars: buyNowPrice,
    selectedQuote,
    currency,
    additionalCharges,
    shipping: listing.shipping ?? null,
    isOffer: false,
    isBuyNow: true,
    selectedPaymentMethod,
    isWireSelected,
    isCheapestCarrier,
    totalTaxCents,
    suppressShippingLine,
    // Ungated: see BidConfirmationModal — the fee describes money B-Stock
    // never processes on these lots, flag or no flag.
    isSellerCollects: isSellerCollectsLot(listing),
  })

  const enforcedTotalPaymentInfo =
    showAutoPayUI && totalPaymentInfo[0]
      ? [
          {
            ...totalPaymentInfo[0],
            description: formatMessage({
              id: 'TotalPaymentInfo.estimatedTotalDescriptionAutoPayBuyNow',
            }),
          },
          ...totalPaymentInfo.slice(1),
        ]
      : totalPaymentInfo

  const hasAnyDigitalMethods = paymentMethods.length > 0

  const {
    data: sellerCollectsSubscription,
    isLoading: isSellerCollectsSubscriptionLoading,
    isError: isSellerCollectsSubscriptionError,
  } = activeSellerSubscriptionServiceQuery.useQuery(listing.accountId, {
    enabled: isSellerCollectsSale,
  })
  const sellerCollectsFeeModel = getFeeModel({
    isLoggedIn: true,
    isLoading: isSellerCollectsSubscriptionLoading,
    isError: isSellerCollectsSubscriptionError,
    buyersPremium: sellerCollectsSubscription?.buyersPremium,
  })
  const sellerCollectsHasBuyerPremium = feeModelHasBuyerPremium(
    sellerCollectsFeeModel
  )
  // The card BCD-3 shows, which is not always the selected method: that one
  // falls back to the account default, and a default of ACH or Net Terms can
  // never be charged the fee. The picker opens on this card so it opens on
  // what the buyer just saw, rather than on a method it greys out.
  const sellerCollectsFeeCard = findFeeCard(
    paymentMethods,
    selectedPaymentMethod
  )
  const sellerCollectsName = getSellerCollectsSellerName(listing.seller)
  const sellerCollectsConfig = getSellerCollectsConfig(listing)
  // BCD-3's Add a card / Change / Add a card instead all open this picker, and
  // `showAutoPayUI` excludes Seller Collects by design — without this the
  // buttons would set state and show nothing.
  const showSellerCollectsFeeSection =
    isSellerCollectsSale && sellerCollectsHasBuyerPremium !== undefined
  const showPaymentMethodPicker =
    (showAutoPayUI || showSellerCollectsFeeSection) && isPaymentMethodModalOpen
  const sellerCollectsPaymentInfo = useSellerCollectsPaymentInfo({
    bidAmountDollars: buyNowPrice,
    bstockFeeCents: additionalCharges.bstockFee,
    currency,
    feeModel: sellerCollectsFeeModel ?? 'unknownFee',
    paymentDeadlineDays: getPaymentDeadlineDays(sellerCollectsConfig),
    saleKind: 'purchase',
    sellerName: sellerCollectsName,
    sellerShippingCents: getSellerBilledShippingCents(
      bStockQuotesShipping(sellerCollectsConfig),
      selectedQuote
    ),
  })
  // Only once the fee model is known: the two models group the total
  // differently, and showing one then the other would move money on screen.
  const showSellerCollectsTotal =
    isSellerCollectsSale && sellerCollectsFeeModel !== undefined
  // The shipping-fee estimate and driver-unload-limits notice are only true
  // when B-Stock itself quotes and books the freight; on every other Seller
  // Collects arrangement (buyer arranged, seller delivers) neither claim
  // holds, same rule `bStockQuotesShipping` already applies
  // to the shipping cost line.
  const showEstimatedShippingInfo =
    !isSellerCollectsSale || bStockQuotesShipping(sellerCollectsConfig)

  const payRestrictions = usePaymentRestrictions()
  const isNonUsdCurrency = !!currency && currency.toUpperCase() !== 'USD'
  const isSelectedMethodRestricted =
    autoPayEnabledForListing &&
    selectedPaymentMethod !== null &&
    isPaymentMethodDisabled(selectedPaymentMethod.type, {
      ach: isNonUsdCurrency || payRestrictions.isACHBlocked,
      card: isNonUsdCurrency || payRestrictions.isCreditCardBlocked,
      netTerms: isNonUsdCurrency || payRestrictions.isNetTermsBlocked,
    })

  const hasSelectedDigitalMethod = !!selectedPaymentMethod
  // FP-1910/1906: block Confirm while a parcel rate selection is in flight, the
  // rate set is still loading, or payment is blocked (no-rates / query error).
  const enforcedIsConfirmDisabled =
    isSelectedMethodRestricted ||
    deliveryMethodChangePending ||
    isParcelSelectionPending ||
    isParcelRatesLoading ||
    parcelPaymentBlocked ||
    (!hasSelectedDigitalMethod && !isWireSelected)

  const handleEnforcedClose = () => {
    closeModal()
  }

  // FP-3784: the Seller Collects fee section's "Add a card" opens the credit
  // card form directly; the picker only hosts the card widget.
  const [opensOnCardForm, setOpensOnCardForm] = useState(false)

  const handlePaymentMethodClick = () => {
    setOpensOnCardForm(false)
    setIsPaymentMethodModalOpen(true)
  }

  const sellerCollectsFeeAccess = getSellerCollectsFeeAccess({
    isSellerCollects: isSellerCollectsSale,
    enableBalanceCreditCards: enableBalanceCreditCards,
    currency,
    isAccountCardBlocked: payRestrictions.isCreditCardBlocked,
  })

  const handleSellerCollectsAddCard = () => {
    setOpensOnCardForm(true)
    setIsPaymentMethodModalOpen(true)
  }

  const handlePaymentMethodConfirm = (paymentMethodId: string) => {
    setAutoPayMethodId(paymentMethodId)
    // The context is AutoPay's persisted selection. Seller Collects now opens
    // this picker with AutoPay off, and a fee card is no auto-pay preference.
    if (autoPayEnabledForListing) setSelectedPaymentMethodId(paymentMethodId)
    setIsWireSelected(false)
    setIsPaymentMethodModalOpen(false)
  }

  const handleAddPaymentMethodClick = () => {
    setIsPaymentMethodModalOpen(true)
  }

  const handleWireConfirmFromModal = () => {
    setIsWireSelected(true)
    setAutoPayMethodId(null)
    if (autoPayEnabledForListing) setSelectedPaymentMethodId(null)
    setIsPaymentMethodModalOpen(false)
  }

  const handleShippingChangeClick = () => {
    editDeliveryMethod({
      ...shippingTrackingCallbacks,
      redirectURI: getEditDeliveryMethodRedirectUri(),
    })
  }

  // Seller Collects lots only ever reach the legacy footer, since
  // `showAutoPayUI` excludes them — so this cannot live in one of them alone.
  const trackSellerCollectsConfirm = () => {
    if (!listingIsSellerCollects) return
    trackSellerCollectsEvent(SellerCollectsEvent.BidConfirmed, {
      ...sellerCollectsListingContext(
        listing,
        buyerPremiumAppliesFor(sellerCollectsFeeModel)
      ),
      // What was confirmed, not what the listing offers: bought outright, whatever the listing's own sale kind says.
      saleKind: 'purchase',
    })
  }

  const handleConfirmBuyNowClick = () => {
    trackSellerCollectsConfirm()
    trackBuyNowConfirm({
      selectedQuote,
      selectedPaymentMethod,
      isWireSelected,
      anyAddressWithinRadius,
      costContext,
    })
    onBuyNowConfirm()
  }

  // Legacy content — original JSX with an added isContract guard
  const renderLegacyContent = () => {
    return (
      <>
        <ModalContent>
          <HeaderDescription data-testid="buy-now-header-description">
            {/* B-Stock collects nothing here beyond its own fee, so the
                standard intro's "pay the full amount due immediately" and
                "complete payment" on the order page are both wrong. */}
            {showSellerCollectsTotal ? (
              <FormattedMessage
                id={SELLER_COLLECTS_INTRO_IDS[sellerCollectsFeeModel]}
                values={{ sellerName: sellerNameValue(sellerCollectsName) }}
              />
            ) : (
              <FormattedMessage id={`${ns}.header.desc`} />
            )}
          </HeaderDescription>
          {/* BCD-3 applies to the purchase as much as the bid — FP-3151 only
              ever built it for the bid modal, leaving a Buy Now buyer with no
              way to save the card their fee invoice is charged to. */}
          {showSellerCollectsFeeSection && (
            <SellerCollectsFeeSection
              hasBuyerPremium={sellerCollectsHasBuyerPremium}
              sellerName={sellerCollectsName || undefined}
              savedMethod={sellerCollectsFeeCard}
              defaultMethodId={defaultPaymentMethod?.id}
              saleKind="purchase"
              onPaymentMethodClick={handlePaymentMethodClick}
              onAddCardClick={handleSellerCollectsAddCard}
              canAddCard={sellerCollectsFeeAccess.canAddCard}
              canChangeCard={sellerCollectsFeeAccess.canChangeCard}
            />
          )}
          <BuyNowDetailsSection>
            <BuyNowDetailsSectionContainer>
              {/* The design shows the price back only inside the estimated
                  total; a second amount row above reads as a second bill. */}
              {!showSellerCollectsTotal && (
                <BuyNowOrderAmount>
                  <OrderAmountTitle>
                    <FormattedMessage id={`${ns}.buyNowAmount.title`} />
                  </OrderAmountTitle>
                  <OrderAmountTotal>
                    <BuyNowAmount
                      listing={listing}
                      buyNowPrice={buyNowPrice}
                      currency={currency}
                    />
                  </OrderAmountTotal>
                </BuyNowOrderAmount>
              )}
              {/* Suppress the generic LTL/TL "no shipping fee" warning when the
                  parcel no-rates banner is blocking payment (it contradicts the
                  hard block). */}
              {/* Never on Seller Collects: B-Stock adds no shipping fee to
                  this total, and no bid is being placed. */}
              {isZeroQuoteValue &&
                !parcelPaymentBlocked &&
                !isSellerCollectsSale && (
                  <>
                    <StyledBanner type="warning">
                      <FormattedMessage id="BidConfirmationModal.warning.noShippingFee" />
                    </StyledBanner>
                    <ShippingChangeButtonRow>
                      <ChangeButton
                        data-testid="buy-now-shipping-change-button"
                        appearance={
                          deliveryMethodChangePending ? 'loading' : 'secondary'
                        }
                        size="small"
                        type="button"
                        onClick={handleShippingChangeClick}
                        disabled={deliveryMethodChangePending}
                      >
                        <FormattedMessage id={`${bidNs}.shipping.change`} />
                      </ChangeButton>
                    </ShippingChangeButtonRow>
                  </>
                )}
              {showCcAcceptedBanner &&
                !isSellerCollectsSale &&
                (isZeroQuoteValue ? (
                  <SlimBanner type="info">
                    <FormattedMessage id="BidConfirmationModal.creditCardAcceptedBanner" />
                  </SlimBanner>
                ) : (
                  <StyledBanner type="info">
                    <FormattedMessage id="BidConfirmationModal.creditCardAcceptedBanner" />
                  </StyledBanner>
                ))}
              {/* Contract Buy Now defers tax/fee breakdown to the fulfilment
                  order — mirrors the `!isContract` guard the Auction legacy
                  modal already applies to this same CardOrder. */}
              {/* FP-1910: on the parcel path, scope the polite live region to
                  just the order total so a rate switch announces the new total
                  without re-reading the whole rate list. Non-parcel layout is
                  unchanged. */}
              {parcelRateList ? (
                <>
                  {parcelRateList}
                  <div aria-live="polite">
                    {!isContract && (
                      <CardOrderContainer>
                        <CardOrder
                          data={
                            showSellerCollectsTotal
                              ? sellerCollectsPaymentInfo
                              : totalPaymentInfo
                          }
                          autoCollapseOnDataChange
                        />
                      </CardOrderContainer>
                    )}
                  </div>
                </>
              ) : (
                !isContract && (
                  <CardOrderContainer>
                    <CardOrder
                      data={
                        showSellerCollectsTotal
                          ? sellerCollectsPaymentInfo
                          : totalPaymentInfo
                      }
                      autoCollapseOnDataChange
                    />
                  </CardOrderContainer>
                )
              )}
              {showSellerCollectsTotal &&
                !isContract &&
                sellerCollectsFeeModel === 'feeBearing' && (
                  <SellerCollectsTotalNote
                    sellerName={sellerCollectsName}
                    sellerInvoicesBuyer={
                      sellerCollectsConfig?.sellerInvoicesBuyer
                    }
                    paysViaRep={paysViaSellerRep(
                      sellerCollectsConfig?.acceptedPaymentMethods
                    )}
                  />
                )}
              {/* Confirm Max Bid's equivalent legacy path already renders
                  this for auctions — Buy Now's legacy path was missing it. */}
              {!isContract && (
                <MaxTaxExposureDisplayContainer
                  listingId={listing._id}
                  buyerAddressId={selectedQuote?.destination.id}
                  currencyCode={currency}
                  buyNowPrice={buyNowPrice}
                  buyerFeeCents={additionalCharges.bstockFee}
                  shippingCents={selectedQuote?.totalPrice}
                  shipmentType={selectedQuote?.shipmentType}
                />
              )}
              {/* FR-008 EU VAT counterpart. Each container self-gates on
                  currency (CAD/USD vs EUR), so at most one renders. */}
              {!isContract && (
                <MaxVatExposureDisplayContainer
                  listingId={listing._id}
                  buyerAddressId={selectedQuote?.destination.id}
                  currencyCode={currency}
                  buyNowPrice={buyNowPrice}
                  buyerFeeCents={additionalCharges.bstockFee}
                  shippingCents={selectedQuote?.totalPrice}
                  shipmentType={selectedQuote?.shipmentType}
                />
              )}
            </BuyNowDetailsSectionContainer>
          </BuyNowDetailsSection>
          {showEstimatedShippingInfo && (
            <Banner title={<FormattedMessage id={`${ns}.info.title`} />}>
              <InfoList>
                <li>
                  <FormattedMessage id="BidConfirmationModal.info.estimatedShippingFee" />
                </li>
                <li>
                  <FormattedMessage id="BidConfirmationModal.info.unloadLimits" />
                </li>
              </InfoList>
            </Banner>
          )}
        </ModalContent>
        <ActionContainer>
          <Checklist>
            <DetailsValue>
              {showSellerCollectsTotal ? (
                <FormattedMessage
                  id={`${SELLER_COLLECTS_NS}.purchase.termsAcknowledgement`}
                  values={{ link: TermsOfPurchaseLink }}
                />
              ) : (
                <FormattedMessage
                  id={`${ns}.footer.desc`}
                  values={{ link: TermsOfPurchaseLink }}
                />
              )}
            </DetailsValue>
          </Checklist>
          <ButtonRow $even={showSellerCollectsTotal}>
            <Button
              type="button"
              size="medium"
              appearance={showSellerCollectsTotal ? 'secondary' : 'tertiary'}
              onClick={closeModal}
            >
              <FormattedMessage id="Common.cancel" />
            </Button>
            <SubmitButton
              type="button"
              size="medium"
              disabled={
                isParcelSelectionPending ||
                isParcelRatesLoading ||
                parcelPaymentBlocked
              }
              onClick={() => {
                trackSellerCollectsConfirm()
                trackBuyNowConfirm({
                  selectedQuote,
                  anyAddressWithinRadius,
                  costContext,
                })
                onBuyNowConfirm()
              }}
              appearance={isParcelSelectionPending ? 'loading' : 'primary'}
            >
              <FormattedMessage id={`${ns}.footer.confirmBuyNow`} />
            </SubmitButton>
          </ButtonRow>
        </ActionContainer>
      </>
    )
  }

  if (showQuoteLoading)
    return isBuyerArrangedDefault ? null : <QuoteLoadingSpinner />

  if (bidBlockedOnQuote) return null

  return (
    <>
      <BuyNowModal
        allowContentOverflow={showAutoPayUI}
        closeModal={showAutoPayUI ? handleEnforcedClose : closeModal}
      >
        {showPaymentMethodPicker ? (
          <PaymentMethodSelectionModal
            isOpen={isPaymentMethodModalOpen}
            onClose={() => setIsPaymentMethodModalOpen(false)}
            onConfirm={handlePaymentMethodConfirm}
            accountId={buyerAccountId ?? ''}
            selectedPaymentMethodId={
              showSellerCollectsFeeSection
                ? (sellerCollectsFeeCard?.id ?? autoPayMethodId)
                : autoPayMethodId
            }
            payableAmountInCents={payableAmountInCents}
            listingIsCreditCardEnabled={listingIsCreditCardEnabled}
            initialIsWireSelected={isWireSelected}
            currency={currency}
            onWireConfirm={
              hasAnyDigitalMethods ? handleWireConfirmFromModal : undefined
            }
            isSellerCollects={showSellerCollectsFeeSection}
            startWithCardForm={opensOnCardForm}
            saleKind="purchase"
            renderActions={({ onCancel, onConfirm, isConfirmDisabled }) => (
              <FooterContainer>
                <FooterButtonRow>
                  <FooterCancelButton appearance="secondary" onClick={onCancel}>
                    <FormattedMessage
                      id={`${bidNs}.autoPay.modal.cancelButton.label.enforced`}
                    />
                  </FooterCancelButton>
                  <FooterConfirmButton
                    disabled={isConfirmDisabled}
                    onClick={onConfirm}
                    appearance="primary"
                  >
                    <FormattedMessage
                      id={`${bidNs}.autoPay.modal.confirmButton.label.enforced`}
                    />
                  </FooterConfirmButton>
                </FooterButtonRow>
              </FooterContainer>
            )}
          />
        ) : (
          <>
            <Modal.Header>
              <FormattedMessage id={`${ns}.header.title`} />
            </Modal.Header>
            {showAutoPayUI ? (
              <AutoPayConfirmContent
                isACHBlocked={payRestrictions.isACHBlocked}
                isBuyNow
                isContract={isContract}
                buyNowPrice={buyNowPrice}
                maxBidAmountValue={
                  <BuyNowAmount
                    listing={listing}
                    buyNowPrice={buyNowPrice}
                    currency={currency}
                  />
                }
                currency={currency}
                listingId={listing._id}
                actualEndTime={displayEndTime}
                selectedQuote={selectedQuote}
                termsOfPurchaseUrl={
                  listing.documents?.find(
                    ({ docType }) =>
                      docType === DocumentDtoDocTypeEnum.TermsOfService
                  )?.url
                }
                selectedPaymentMethod={selectedPaymentMethod}
                isSelectedMethodRestricted={isSelectedMethodRestricted}
                isSavedMethodInvalid={isSavedMethodInvalid}
                hasAnyDigitalMethods={hasAnyDigitalMethods}
                isZeroQuoteValue={isZeroQuoteValue}
                isWireSelected={isWireSelected}
                showAchBlockedWireFallbackMessage={
                  showAchBlockedWireFallbackMessage
                }
                onWireTransferChange={() => setIsWireSelected(!isWireSelected)}
                onPaymentMethodClick={handlePaymentMethodClick}
                onAddPaymentMethodClick={handleAddPaymentMethodClick}
                onShippingChangeClick={handleShippingChangeClick}
                shippingChangePending={deliveryMethodChangePending}
                groups={listing.lot.groups}
                onCarrierChangeClick={
                  shouldShowCarrierChange
                    ? () => setIsChangeCarrierModalOpen(true)
                    : undefined
                }
                isCarrierChangeDisabled={
                  !hasMultipleCarrierOptions ||
                  isChangingCarrier ||
                  isCarrierChangePending
                }
                isCarrierChangeLoading={
                  isCarrierChangePending || isChangingCarrier
                }
                hasMultipleCarrierOptions={hasMultipleCarrierOptions}
                isCheapestCarrier={isCheapestCarrier}
                parcelRateList={parcelRateList}
                parcelPaymentBlocked={parcelPaymentBlocked}
                parcelSelectionPending={isParcelSelectionPending}
                totalPaymentInfo={enforcedTotalPaymentInfo}
                isLoading={false}
                enforcedIsConfirmDisabled={enforcedIsConfirmDisabled}
                showCcAcceptedBanner={showCcAcceptedBanner}
                onConfirmBidClick={handleConfirmBuyNowClick}
                onClose={handleEnforcedClose}
                error={null}
                buyerFeeCents={additionalCharges.bstockFee}
              />
            ) : (
              renderLegacyContent()
            )}
          </>
        )}
      </BuyNowModal>

      {(showAutoPayUI || showSellerCollectsFeeSection) &&
        !isPaymentMethodModalOpen && (
          <BalanceWidgetContainer id="balance-sdk" />
        )}

      {isChangeCarrierModalOpen && (
        <ChangeCarrierModal
          {...changeCarrierModalProps}
          onChange={handleCarrierChange}
          onClose={() => setIsChangeCarrierModalOpen(false)}
          isCarrierChangePending={isCarrierChangePending}
        />
      )}
    </>
  )
}

export default BuyNowConfirmationModal
